import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { tripByCities } from "@/app/Cities/Trips/GraphQL/tripByCities";
import { TripMap } from "@/app/Cities/Trips/Components/TripMap";
import { TripStats } from "@/app/Cities/Trips/Components/TripStats";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { TripEditDialog } from "@/app/Cities/Trips/Components/TripEditDialog";
import { Badge } from "@/components/catalyst/badge";
import { Measurement } from "@/gql/graphql";

type AdditionalCity = {
  id: string;
  name: string;
  lat?: number | null;
  lng?: number | null;
};

type FallbackCity = {
  id: string;
  name: string;
  lat?: number | null;
  lng?: number | null;
};

export function TripCard({
  cityAId,
  cityBId,
  additionalCities = [],
  fallbackCityA,
  fallbackCityB,
  measurement,
}: {
  cityAId: string;
  cityBId: string;
  additionalCities?: AdditionalCity[];
  fallbackCityA?: FallbackCity;
  fallbackCityB?: FallbackCity;
  measurement?: Measurement;
}) {
  const { data, isLoading, isError } = useGqlQuery(tripByCities, {
    cityAId,
    cityBId,
  });

  const trip = data?.tripByCities;
  const { isAdmin } = useTenant();
  const { open } = useDialog(TripEditDialog);

  // Handle loading state
  if (isLoading) {
    return (
      <div className="w-full overflow-hidden rounded-md border border-gray-200">
        <div className="flex h-[240px] items-center justify-center bg-gray-100">
          <span className="text-sm text-gray-500">Loading map...</span>
        </div>
        <div className="h-[50px] bg-gray-50" />
      </div>
    );
  }

  // Handle error or missing trip - show map with fallback cities
  if (isError || !trip) {
    return (
      <div className="w-full overflow-hidden rounded-md border border-gray-200">
        <div className="relative">
          <TripMap
            cityAName={fallbackCityA?.name ?? "Origin"}
            cityBName={fallbackCityB?.name ?? "Destination"}
            cityALat={fallbackCityA?.lat}
            cityALng={fallbackCityA?.lng}
            cityBLat={fallbackCityB?.lat}
            cityBLng={fallbackCityB?.lng}
            additionalWaypoints={additionalCities.map((city) => ({
              name: city.name,
              lat: city.lat,
              lng: city.lng,
            }))}
          />
        </div>
        <div className="bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No trip route found between these cities. Distance and duration
          unavailable.
        </div>
      </div>
    );
  }

  const { cityA, cityB, distance_kms, duration_hrs, route_name, id } = trip;

  // tripByCities returns the trip in canonical (stored) order, which is not
  // necessarily origin→destination. Orient by the requested origin id so the
  // "A" marker always lands on the departure city.
  const originCity = cityA.id === cityAId ? cityA : cityB;
  const destCity = cityA.id === cityAId ? cityB : cityA;

  return (
    <div className="w-full overflow-hidden rounded-md border border-gray-200">
      <div className="relative">
        <TripMap
          cityAName={originCity.name}
          cityBName={destCity.name}
          cityALat={originCity.lat}
          cityALng={originCity.lng}
          cityBLat={destCity.lat}
          cityBLng={destCity.lng}
          additionalWaypoints={additionalCities.map((city) => ({
            name: city.name,
            lat: city.lat,
            lng: city.lng,
          }))}
        />
        {isAdmin ? (
          <div className="absolute right-2 top-2 z-[1000]">
            <Button
              outline
              className="bg-white shadow-md"
              onClick={() => {
                open({ tripId: id });
              }}
            >
              Change Route
              <Badge color="sky">admin</Badge>
            </Button>
          </div>
        ) : null}
      </div>
      <TripStats
        distance_kms={distance_kms}
        duration_hrs={duration_hrs}
        route_name={route_name}
        measurement={measurement}
      />
    </div>
  );
}
